import { useEffect, useState } from 'react'
import { Download, X } from 'lucide-react';
import { RadioButton } from '@/components/common/radio-button'
import { IconButton } from '@/components/common/icon-button';
import { TrashIcon } from '@/components/ui/icon';
import { Tooltip } from "@/components/ui/modal";
import { useTranslation } from 'react-i18next';
import { MoveIcon } from '../assets-material-item/assets';
import { ConfirmModal } from './confirm-modal';
import { cn } from '@/lib/utils';

interface AssetsToolBarProps {
  toolNode?: React.ReactNode;
  /** 选中项数量 */
  selectedAmount?: number;
  /** 是否选中所有项 */
  isSelectedAll?: boolean;
  /** 是否显示删除选项，默认显示 */
  showDelete?: boolean;
  /** 是否显示下载选项 */
  showDownload?: boolean;
  /** 是否显示移动选项 */
  showMove?: boolean;
  /** 删除按钮标题 */
  deleteTitle?: string;
  /** 删除按钮描述 */
  deleteDesc?: string;
  /** 删除按钮加载中 */
  deleteLoading?: boolean;
  /** 选中所有项回调 */
  onSelectedAll?: (checked: boolean) => void;
  /** 删除选中项回调 */
  onDelete?: () => Promise<void> | void;
  /** 下载选中项回调 */
  onDownload?: () => void;
  /** 移动选中项回调 */
  onMove?: () => void;
  /** 关闭 */
  onClose?: () => void;
}

/** 浮层工具栏 */
export const AssetsToolBar = ({
  toolNode,
  selectedAmount,
  isSelectedAll,
  showMove,
  showDelete = true,
  showDownload,
  deleteTitle,
  deleteDesc,
  deleteLoading,
  onSelectedAll,
  onDelete,
  onDownload,
  onMove,
  onClose,
}: AssetsToolBarProps) => {
  const { t } = useTranslation();
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [allChecked, setAllChecked] = useState(isSelectedAll || false);

  useEffect(() => {
    setAllChecked(isSelectedAll || false);
  }, [isSelectedAll]);

  // 显示删除确认弹窗
  const handleShowDeleteConfirmModal = () => {
    if (!selectedAmount) return;
    setShowDeleteConfirmModal(true);
  }

  // 删除确认
  const handleDeleteConfirm = async () => {
    if (!selectedAmount) return;
    // 判断 onDelete 是否是异步函数，仅检查async/await
    if (onDelete.constructor.name === "AsyncFunction") {
      await onDelete();
    } else {
      onDelete();
    }
  }

  // 下载选中项
  const handleDownload = () => {
    if (!selectedAmount) return;
    onDownload?.();
  }

  // 移动选中项
  const handleMove = () => {
    if (!selectedAmount) return;
    onMove?.();
  }

  return (
    <div assets-toolbar className="w-[386px] h-[50px] absolute left-1/2 transform -translate-x-1/2 bottom-0 bg-[#101828] rounded-md shadow-soft">
      <div className='w-full h-full flex items-center justify-between px-4'>
        <div className='flex items-center'>
          <RadioButton
            label={t('assets.material.all_select')}
            checked={allChecked}
            onChange={(checked) => {
              setAllChecked(checked);
              onSelectedAll?.(checked);
            }}
            className='border-2 border-white bg-transparent'
            labelClassName='text-white'
          />
          <span className='text-white text-xs ml-2'>{t('assets.material.already_select')} {selectedAmount}</span>
        </div>
        <div className='flex items-center gap-3'>
          {showDownload && <Tooltip
            side='top'
            align='center'
            offset={20}
            trigger={
              <IconButton
                className='hover:bg-white/30 size-8 rounded-[8px]'
                icon={<Download className='text-white size-5' />}
                onClick={handleDownload}
              />
            }
            content={
              <div className='relative bg-[rgba(16,24,40,1)] px-3 py-2 rounded-[8px]'>
                <span className='text-[rgba(255,255,255,1)] text-sm font-medium'>
                  {t('assets.material.download_select')}
                </span>
                <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-l-transparent border-r-transparent border-t-black"></div>
              </div>
            }
          />}
          {showMove && <Tooltip
            side='top'
            align='center'
            offset={20}
            trigger={
              <IconButton
                className='hover:bg-white/30 size-8 rounded-[8px]'
                icon={<MoveIcon className='text-white size-5' />}
                onClick={handleMove}
              />
            }
            content={
              <div className='relative bg-[rgba(16,24,40,1)] px-3 py-2 rounded-[8px]'>
                <span className='text-[rgba(255,255,255,1)] text-sm font-medium'>
                  {t('assets.material.move_select')}
                </span>
                <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-l-transparent border-r-transparent border-t-black"></div>
              </div>
            }
          />}
          {toolNode}
          {showDelete && <Tooltip
            side='top'
            align='center'
            offset={20}
            trigger={
              <IconButton
                className='hover:bg-white/30 size-8 rounded-[8px]'
                icon={<TrashIcon className='text-white size-5' />}
                onClick={handleShowDeleteConfirmModal}
              />
            }
            content={
              <div className='relative bg-[rgba(16,24,40,1)] px-3 py-2 rounded-[8px]'>
                <span className='text-[rgba(255,255,255,1)] text-sm font-medium'>
                  {t('assets.material.delete_select')}
                </span>
                <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-l-transparent border-r-transparent border-t-black"></div>
              </div>
            }
          />}
          <Tooltip
            side='top'
            align='center'
            offset={20}
            trigger={
              <IconButton
                className='hover:bg-white/30 size-8 rounded-[8px]'
                icon={<X className='text-white size-5' />}
                onClick={onClose}
              />
            }
            content={
              <div className='relative bg-[rgba(16,24,40,1)] px-3 py-2 rounded-[8px]'>
                <span className='text-[rgba(255,255,255,1)] text-sm font-medium'>
                  {t('assets.material.cancel_multiple_select')}
                </span>
                <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-l-transparent border-r-transparent border-t-black"></div>
              </div>
            }
          />
        </div>
      </div>
      {showDeleteConfirmModal && (
        <ConfirmModal
          open={showDeleteConfirmModal}
          title={deleteTitle || t('assets.material_item.delete_title')}
          description={deleteDesc || t('assets.material_item.delete_desc', { count: selectedAmount })}
          cancel={t('common.cancel')}
          confirm={t('assets.material_item.confirm_button')}
          onConfirm={handleDeleteConfirm}
          onOpenChange={setShowDeleteConfirmModal}
          loading={deleteLoading}
        />
      )}
    </div>
  )
}
